// Story 11.12 (ADO-1963), T4 (AC1, AC4): the PDP's "cheaper on WOW" action.
//
// It replaces the hero's disabled "WOW Price" placeholder (Bug 3739). It renders nothing unless
// the product is cheaper at the store's WOW entry than at the session's active entry
// (`useWowOffer`, comparing the PDP's own price-store quote with an isolated WOW read). Then it
// is a button naming the WOW price; pressing it adds the product to the store's WOW order
// (`useWowOrderAdd`), never to the order already started, and never changes the active entry.
//
// After an add: "Added to your WOW order" with the price the server locked on the line (in a status
// region that is on screen, empty, from the first render, so it is announced when filled), and
// "View your WOW order". Following it is the person's explicit choice of the WOW entry and the
// only place this story changes the active entry: `useOpenOrder` makes the WOW entry active and
// opens its order review, through the product page's own open (`ProductDetail`), so the page's
// depot-switch redirect does not send the person to the product list instead. It is a button,
// not a link: it changes the session before it navigates (as the HHT summary's open-cart
// buttons do), which a link opened in a new tab would do without the navigation. When the WOW
// entry can no longer be chosen, nothing changes, the person is told, and the offer is read
// again. A product already on the WOW order is not added at once: the line and its locked price
// are shown, with "Add 1 more".
//
// It is a fragment of the hero's actions row: the button sits beside Add, and the messages wrap
// onto their own line under them. The fragment always has the same shape, the status region in
// the same place, whether or not the offer is shown, so a change of the comparison during an add
// never remounts the region already filled (an announcement could be missed). The button's
// accessible name starts with its visible text ("WOW €7.50, add ... to your WOW order",
// WCAG 2.5.3).

import { useState } from 'react';
import { formatMoney } from '../../shared/money';
import { comparableFromPriceEntry } from '../../pricing/cheaperOnWow';
import { usePriceEntry } from '../../pricing/usePriceStore';
import { useWowOffer, type WowOfferContext } from '../../pricing/useWowOffer';
import { wowAddMessage, type WowAddResult } from '../../basket/addToWowOrder';
import { useWowOrderAdd } from '../../basket/useWowOrderAdd';
import { useOpenOrder } from './openOrder';
import styles from './ProductHero.module.css';

/** Said when "View your WOW order" finds the WOW entry can no longer be chosen. */
export const WOW_ORDER_UNOPENED_MESSAGE = "Your WOW order can't be opened right now.";

export interface WowOfferActionProps {
  readonly sku: string;
  readonly productName: string;
}

/** The confirmation of a written add, naming the price the server locked on the line. */
function addedText(result: Extract<WowAddResult, { kind: 'added' }>): string {
  const price =
    result.line.unitPrice === undefined ? '' : ` at ${formatMoney(result.line.unitPrice)}`;
  return result.line.quantity > 1
    ? `Added to your WOW order: now ${result.line.quantity}${price}.`
    : `Added to your WOW order${price}.`;
}

function WowOrderLink({
  context,
  onRefused,
}: {
  readonly context: WowOfferContext;
  readonly onRefused: () => void;
}) {
  const openOrder = useOpenOrder();
  return (
    <button
      type="button"
      className={styles.wowOrderLink}
      onClick={() => {
        // The person chose the WOW order: make it the active one, then review it.
        if (!openOrder(context.entry.key, context.customerAccountCode)) onRefused();
      }}
    >
      View your WOW order
    </button>
  );
}

export function WowOfferAction({ sku, productName }: WowOfferActionProps) {
  const active = comparableFromPriceEntry(usePriceEntry(sku));
  const offer = useWowOffer(sku, active);
  const add = useWowOrderAdd(sku, offer.context, offer.reread);
  const busy = add.phase.phase === 'busy';
  const result = add.phase.phase === 'settled' ? add.phase.result : null;
  const context = offer.context;
  // The settled add whose "View your WOW order" was refused (so a later add starts clean).
  const [unopened, setUnopened] = useState<WowAddResult | null>(null);
  const openRefused = result !== null && unopened === result;
  const onOpenRefused = () => {
    if (result === null) return;
    setUnopened(result);
    offer.reread();
  };

  // The status region stays on screen, empty, so a screen reader announces what fills it.
  const statusText =
    result === null
      ? ''
      : result.kind === 'added'
        ? addedText(result)
        : result.kind === 'alreadyOnOrder'
          ? (wowAddMessage(result) ?? '')
          : '';
  const statusRegion = (
    <p
      className={statusText === '' ? styles.visuallyHidden : styles.wowStatus}
      role="status"
      data-wow-status=""
    >
      {statusText}
    </p>
  );

  // With no WOW context there is no offer and no outcome (the add's is dropped with it).
  const shown = context === null ? null : result;
  const message = openRefused
    ? WOW_ORDER_UNOPENED_MESSAGE
    : shown === null
      ? null
      : wowAddMessage(shown);
  const isProblem =
    openRefused ||
    (shown !== null &&
      (shown.kind === 'refused' ||
        shown.kind === 'forbidden' ||
        shown.kind === 'conflict' ||
        shown.kind === 'unread' ||
        shown.kind === 'uncertain'));

  // Always this shape: the button's slot, the status region, the messages' slot.
  return (
    <>
      {context !== null && offer.comparison.cheaper && (
        <button
          type="button"
          className={styles.wowPrice}
          data-control="wow"
          disabled={busy}
          aria-label={`WOW ${formatMoney(offer.comparison.wowUnitPrice)}, add ${productName} to your WOW order`}
          onClick={add.start}
        >
          WOW {formatMoney(offer.comparison.wowUnitPrice)}
        </button>
      )}
      {statusRegion}
      {context !== null && result !== null && (
        <div className={styles.wowOfferMessage} data-wow-offer-message="">
          {result.kind === 'alreadyOnOrder' && (
            <button
              type="button"
              className={styles.wowPrice}
              disabled={busy}
              aria-label={`Add 1 more ${productName} to your WOW order`}
              onClick={() => add.addOneMore(result.token, result.revision)}
            >
              Add 1 more
            </button>
          )}
          {result.kind === 'added' && !openRefused && (
            <WowOrderLink context={context} onRefused={onOpenRefused} />
          )}
          {isProblem && message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          {result.kind === 'uncertain' &&
            result.line !== null &&
            result.line !== undefined &&
            !openRefused && <WowOrderLink context={context} onRefused={onOpenRefused} />}
        </div>
      )}
    </>
  );
}
